import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Eye, Glasses } from "lucide-react";
import { useLocation } from "react-router-dom";

// WhatsApp oficial usa este icono simplificado
const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const CENTERS = [
  {
    key: "madrid",
    label: "Madrid",
    tag: null,
    options: [
      {
        key: "terapia",
        label: "Terapia visual",
        desc: "Dudas sobre terapia visual, citas, sesiones…",
        phone: "34657957821",
        message: "Hola, escribo desde Madrid. Me gustaría obtener información sobre terapia visual.",
        icon: Eye,
      },
      {
        key: "optica",
        label: "Óptica",
        desc: "Gafas, lentillas, revisiones, monturas…",
        phone: "34640812328",
        message: "Hola, escribo desde Madrid. Me gustaría obtener información sobre óptica.",
        icon: Glasses,
      },
    ],
  },
  {
    key: "alcobendas",
    label: "Alcobendas",
    options: [
      {
        key: "terapia_alco",
        label: "Terapia visual",
        desc: "Consultas sobre terapia visual en Alcobendas",
        phone: "34657957821",
        message: "Hola, escribo desde Alcobendas. Me gustaría obtener información sobre terapia visual.",
        icon: Eye,
      },
      {
        key: "optica_alco",
        label: "Óptica",
        desc: "Gafas, lentillas y revisiones en Alcobendas",
        phone: "34629353459",
        message: "Hola, escribo desde Alcobendas. Me gustaría obtener información sobre óptica.",
        icon: Glasses,
      },
    ],
  },
];

const DEFAULT_MSG = "Hola, me gustaría obtener más información.";

const WhatsAppFloat = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef(null);

  // Ocultar en /admin para no molestar la gestión
  const hidden = location.pathname.startsWith("/admin");

  // Cerrar al hacer click fuera o pulsar ESC
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (hidden) return null;

  const buildUrl = (phone, message) => `https://wa.me/${phone}?text=${encodeURIComponent(message || DEFAULT_MSG)}`;

  return (
    <div ref={ref} className="fixed z-[60] bottom-5 right-5 md:bottom-7 md:right-7" data-testid="whatsapp-float-root">
      {/* Panel de opciones */}
      {open && (
        <div
          className="mb-3 w-[310px] md:w-[360px] rounded-2xl overflow-hidden"
          style={{
            background: "var(--cream)",
            border: "1px solid var(--sage-100)",
            boxShadow: "0 25px 60px -20px rgba(54,66,32,0.4)",
            animation: "fadeUp 0.25s ease-out",
          }}
          data-testid="whatsapp-float-panel"
        >
          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between" style={{ background: "#128C7E", color: "var(--cream)" }}>
            <div className="flex items-center gap-2">
              <WhatsAppIcon size={20} />
              <div>
                <div className="font-sans-body font-semibold text-sm">Elige centro y servicio</div>
                <div className="font-sans-body text-[11px] opacity-85">Te respondemos por WhatsApp</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              className="opacity-80 hover:opacity-100 transition-opacity"
              data-testid="whatsapp-float-close"
            >
              <X size={18} />
            </button>
          </div>
          {/* Opciones agrupadas por centro */}
          <div className="p-3 space-y-3">
            {CENTERS.map((center) => (
              <div key={center.key}>
                {/* Título del centro */}
                <div className="flex items-center justify-between px-1 mb-1.5">
                  <div className="font-sans-body text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--sage-600)" }}>
                    {center.label}
                  </div>
                  {center.tag && (
                    <span className="inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "var(--sage-100)", color: "var(--sage-700)" }}>
                      {center.tag}
                    </span>
                  )}
                </div>

                {/* Opciones del centro */}
                <div className="space-y-1.5">
                  {center.options.map((o) => {
                    const disabled = !o.phone;
                    const commonClasses = "flex items-center gap-3 p-3 rounded-xl transition-colors";
                    const commonStyle = {
                      border: "1px solid var(--sage-100)",
                      background: "var(--bg-soft)",
                      opacity: disabled ? 0.55 : 1,
                    };
                    const content = (
                      <>
                        <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage-100)" }}>
                          <o.icon size={18} style={{ color: "var(--sage-600)" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-sans-body font-semibold text-sm" style={{ color: "var(--ink)" }}>{o.label}</div>
                          <div className="font-sans-body text-xs text-[color:var(--ink-soft)] truncate">
                            {disabled ? "Disponible próximamente" : o.desc}
                          </div>
                        </div>
                        {disabled ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--sage-600)" }}>Pronto</span>
                        ) : (
                          <WhatsAppIcon size={18} />
                        )}
                      </>
                    );
                    if (disabled) {
                      return (
                        <div
                          key={o.key}
                          className={commonClasses}
                          style={{ ...commonStyle, cursor: "not-allowed" }}
                          data-testid={`whatsapp-option-${o.key}`}
                        >
                          {content}
                        </div>
                      );
                    }
                    return (
                      <a
                        key={o.key}
                        href={buildUrl(o.phone, o.message)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={commonClasses}
                        style={commonStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--sage-50)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "var(--bg-soft)"; }}
                        data-testid={`whatsapp-option-${o.key}`}
                      >
                        {content}
                      </a>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 pb-3 font-sans-body text-[11px] text-[color:var(--ink-soft)] text-center space-y-0.5">
            <div className="flex items-center justify-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: "#25D366" }}></span>
              <span style={{ color: "var(--sage-600)", fontWeight: 500 }}>Respondemos en &lt; 2h en horario comercial</span>
            </div>
            <div>L–V · 10:00–14:30 y 16:30–20:00</div>
          </div>
        </div>
      )}

      {/* Botón flotante principal */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Abrir WhatsApp"
        className="relative h-14 w-14 md:h-16 md:w-16 rounded-full flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
        style={{
          background: "#25D366",
          color: "#fff",
          boxShadow: "0 12px 30px -5px rgba(37,211,102,0.55), 0 4px 12px rgba(0,0,0,0.15)",
        }}
        data-testid="whatsapp-float-button"
      >
        {/* pulso sutil */}
        {!open && (
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "#25D366", opacity: 0.35 }} />
        )}
        <span className="relative">
          {open ? <X size={26} /> : <WhatsAppIcon size={28} />}
        </span>
      </button>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppFloat;
